import { chunk } from '@belong/common';
import { IconLicense } from '@belong/icons';
import { Link, Carousel, Image, Modal, Text } from '@belong/ui';
import clsx from 'clsx';
import css from './sell-home-modal.module.css';
import { SELL_HOME_MODAL_STRINGS } from './sell-home-modal.strings';

const SELL_HOME_MODAL_CARDS: CardProps[] = [
  {
    src: 'homeowner-lp/sell_account_image_01.png',
    title: 'Preparing your home for\u00a0listing',
  },
  {
    src: 'homeowner-lp/sell_account_image_02.png',
    title: 'Providing guidance on\u00a0pricing',
  },
  {
    src: 'homeowner-lp/sell_account_image_03.png',
    title: 'Sourcing active buyers across\u00a0all\u00a0markets',
  },
  {
    src: 'homeowner-lp/sell_account_image_05.png',
    title: 'Hosting open homes and\u00a0agent\u00a0tours',
  },
  {
    src: 'homeowner-lp/sell_account_image_04.png',
    title: 'Working with potential\u00a0buyers',
    titleClassName: css.card5Title,
  },
  { src: 'homeowner-lp/sell_account_image_06.png', title: 'Closing\nthe sale' },
];

type SellHomeModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
};

export function SellHomeModal({
  isOpen,
  onDismiss,
}: SellHomeModalProps): JSX.Element {
  return (
    <Modal
      size="fullScreen"
      isOpen={isOpen}
      onDismiss={onDismiss}
      name="HO_SELL_HOME_MODAL"
    >
      <div className="py-3xl md:py-4xl container flex justify-center">
        <div
          className={clsx(
            'flex w-full flex-col items-center text-center',
            css.mainContainer,
          )}
        >
          <div className={clsx('mb-xs md:max-w-none', css.title)}>
            <Text variant="h2" fontWeight="semibold">
              {SELL_HOME_MODAL_STRINGS['sellHomeModal.title']}
            </Text>
          </div>
          <div className="mb-2xl md:mb-3xl">
            <Text>
              {SELL_HOME_MODAL_STRINGS['sellHomeModal.subtitle']}
              <Text as="span" fontWeight="semibold">
                {SELL_HOME_MODAL_STRINGS['sellHomeModal.subtitleBold']}
              </Text>
            </Text>
          </div>
          <div className={clsx('mb-sm md:mb-2xl', css.title2)}>
            <Text variant="h3" fontWeight="semibold">
              {SELL_HOME_MODAL_STRINGS['sellHomeModal.title2']}
            </Text>
          </div>
          <div className="contents md:hidden">
            <div className="mb-2xl w-full">
              <Carousel>
                {SELL_HOME_MODAL_CARDS.map((card, idx) => (
                  <div key={idx} className="flex min-w-full justify-center">
                    <Card {...card} />
                  </div>
                ))}
              </Carousel>
            </div>
          </div>
          <div className="hidden md:contents">
            {chunk(SELL_HOME_MODAL_CARDS, 3).map((cardsChunk, idx) => (
              <div key={idx} className="mb-2xl flex">
                {cardsChunk.map((card, cardIdx) => (
                  <div key={cardIdx} className={css.cardContainer}>
                    <Card {...card} />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="mb-sm flex w-full flex-col items-center md:flex-row md:justify-between">
            <div className="mb-lg flex flex-col md:mb-0 md:text-left">
              <Text>
                {SELL_HOME_MODAL_STRINGS['sellHomeModal.inquireWith']}
              </Text>
              <Text fontWeight="semibold">
                {SELL_HOME_MODAL_STRINGS['sellHomeModal.mail']}
              </Text>
            </div>
            <Link
              variant="solid"
              size={{ _: 'fluid', md: 'default' }}
              href={`mailto:${SELL_HOME_MODAL_STRINGS['sellHomeModal.mail']}?subject=${SELL_HOME_MODAL_STRINGS['sellHomeModal.subject']}`}
            >
              <Text fontWeight="semibold">
                {SELL_HOME_MODAL_STRINGS['sellHomeModal.ctaLabel']}
              </Text>
            </Link>
          </div>
          <div className="md:border-gray md:pt-sm flex w-full items-center justify-center md:justify-end md:border-0 md:border-t md:border-solid">
            <div className="mr-xs">
              <IconLicense width="20" />
            </div>
            <Text variant="p1">
              {SELL_HOME_MODAL_STRINGS['sellHomeModal.realestatelicense']}
            </Text>
          </div>
        </div>
      </div>
    </Modal>
  );
}

type CardProps = {
  src: string;
  title: string;
  titleClassName?: string;
};

function Card({ src, title, titleClassName }: CardProps): JSX.Element {
  return (
    <div className="flex min-w-full flex-col items-center">
      <div className="aspect-h-2 aspect-w-3 w-full">
        <Image src={src} alt="" />
      </div>
      <Text
        fontWeight="semibold"
        className={titleClassName || css.defaultCardTitle}
      >
        {title}
      </Text>
    </div>
  );
}
